import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '@/lib/cn'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Removes the inner padding, for a card whose whole body is a table. */
  isFlush?: boolean
}

export function Card({ className, isFlush = false, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'rounded-card border border-border bg-surface-raised shadow-card',
        !isFlush && 'p-5',
        className,
      )}
      {...props}
    />
  )
}

export interface CardHeaderProps {
  title: ReactNode
  description?: ReactNode
  /** Controls that belong to this card, right-aligned. */
  actions?: ReactNode
  className?: string
  /** The heading level. A card inside a section is usually an h3, not an h2. */
  as?: 'h2' | 'h3'
}

export function CardHeader({
  title,
  description,
  actions,
  className,
  as: Heading = 'h2',
}: CardHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <Heading className="text-[0.9375rem] leading-6 font-semibold text-text">{title}</Heading>
        {description ? (
          <p className="mt-0.5 text-[0.8125rem] leading-5 text-text-secondary">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  )
}
